import { useEffect, useState, useRef } from 'react';

export interface ViewportIntersectionResult {
  arrowTopPosition: number | null;
  isVisible: boolean;
}

const INTERSECTION_THRESHOLDS = Array.from({ length: 11 }, (_, i) => i * 0.1);

export function useViewportIntersection(
  containerRef: React.RefObject<HTMLElement>
): ViewportIntersectionResult {
  const [arrowTopPosition, setArrowTopPosition] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      setArrowTopPosition(null);
      setIsVisible(false);
      return;
    }

    const callback: IntersectionObserverCallback = (entries) => {
      const entry = entries[0];

      if (!entry || !entry.isIntersecting) {
        setArrowTopPosition(null);
        setIsVisible(false);
        return;
      }

      const rect = entry.boundingClientRect;
      const viewportHeight = window.innerHeight;

      // Calculate visible portion
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, viewportHeight);

      // Calculate center of visible portion
      const visibleCenter = (visibleTop + visibleBottom) / 2;

      // Calculate position relative to container's top
      const relativePosition = visibleCenter - rect.top;

      setArrowTopPosition(relativePosition);
      setIsVisible(true);
    };

    observerRef.current = new IntersectionObserver(callback, {
      threshold: INTERSECTION_THRESHOLDS,
    });

    observerRef.current.observe(container);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [containerRef]);

  return {
    arrowTopPosition,
    isVisible,
  };
}
